import { COLORS } from '@btime/constants'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { CnpjOrCpfInput } from './MaskedInput'
import { Text } from '../presentation/Text'
import ActionPlansInput from '../../containers/actionPlans/ActionPlansInput'
import AddressInput from './AddressInput'
import AssetSelect from '../../containers/assets/select/AssetSelect'
import DateInput from './date/DateInput'
import DynamicBlock from '../../containers/dynamic/block/DynamicBlock'
import GalleryInput from './GalleryInput'
import Input from './Input'
import NumberInput from './NumberInput'
import Picker from './Picker'
import PlaceSelect from '../../containers/places/select/PlaceSelect'
import SignatureInput from './SignatureInput'
import TagPicker from './TagPicker'
import TagsInput from './tag/TagsInput'
import Textarea from './Textarea'
import UserSelect from '../../containers/users/select/UserSelect'
import EmailInputWithInterpolation from '../../packages/kiti-editor/components/input/EmailInputWithInterpolation'
import InputWithInterpolations from '../../packages/kiti-editor/components/input/InputWithInterpolations'
import NumberInputWithInterpolation from '../../packages/kiti-editor/components/input/NumberInputWithInterpolation'
import DateInputWithInterpolations from '../../packages/kiti-editor/components/date/DateInputWithInterpolations'
import Select from '../../packages/kiti/components/form/Select'
import { useServiceOrder } from '../../modules/serviceOrders/executionV2/_load/ServiceOrderLoader'
import StockInput from './stock/StockInput'
import DynamicIntegrationInput from './DynamicIntegrationInput'
import ToggleButtonGroupInput from './ToggleButtonGroupInput'
import ImperativeWrapper from './imperative/ImperativeWrapper'
import CoodinatesInput from './CoordinatesInput'

const getNullOption = allowNull => {
  if (!allowNull) return []
  return [{ label: 'N/A', value: null, id: null, name: 'N/A', selectedColor: COLORS.dark3, color: 'dark3' }]
}

const renderInput = ({
  t,
  value,
  allowNull,
  valueType,
  onChange,
  options: initialOptions,
  masks,
  labels,
  interpolations,
  displayAsList,
  filterByPlace,
  place,
  integrationId,
  integrationFieldType,
  requiredDoc,
  ...props
}) => {
  switch (valueType) {
    case 'compliance':
    case 'checkbox':
      const options = [
        { label: labels?.true || t('yes'), value: true, color: 'darkGreen' },
        { label: labels?.false || t('no'), value: false, color: 'red' },
        ...getNullOption(allowNull)
      ]
      return <ToggleButtonGroupInput options={options} onChange={onChange} value={value} {...props} />

    case 'action_plan':
      return <ActionPlansInput onChange={onChange} value={value} {...props} />

    case 'select':
    case 'multiple':
      const withNullOptions = [...(initialOptions || []), ...getNullOption(allowNull && valueType === 'select')]
      const pickerOptions = withNullOptions.map(item => ({ value: item, label: item.name, origin: item }))

      if (displayAsList) {
        return (
          <Select
            options={withNullOptions}
            onChange={onChange}
            idField="id"
            labelField="name"
            multiple={valueType === 'multiple'}
            value={value}
            disabled={valueType === 'multiple' && value === null}
            {...props}
          />
        )
      }

      return (
        <Picker
          options={pickerOptions}
          idField="id"
          onChange={onChange}
          value={value}
          multiple={valueType === 'multiple'}
          {...props}
        />
      )

    case 'datetime':
    case 'date':
      if (!!interpolations.length)
        return (
          <DateInputWithInterpolations
            useTime={valueType === 'datetime'}
            value={value}
            onChange={onChange}
            interpolations={interpolations}
          />
        )
      return <DateInput value={value} onChange={onChange} useTime={valueType === 'datetime'} {...props} />

    case 'system_status':
      const tagPickerOptions = [
        { label: t('active'), value: true, selectedColor: COLORS.green },
        { label: t('inactive'), value: false, selectedColor: COLORS.red }
      ]
      return <TagPicker options={tagPickerOptions} onStopChanging={onChange} value={value} {...props} />

    case 'sign':
      return <SignatureInput onChange={onChange} value={value} requiredDoc={requiredDoc} {...props} />

    case 'signature':
      return <SignatureInput profileOnly onChange={onChange} value={value} {...props} />

    case 'gallery':
      return <GalleryInput useRotation value={value} onChange={onChange} {...props} />

    case 'informative':
      return false

    case 'calc':
      return <Textarea rows={1} value={value} onStopTyping={onChange} disabled={true} {...props} />

    case 'integer':
    case 'numeric':
      return <NumberInput step={1} onlyInteger value={value} onStopTyping={onChange} {...props} />

    case 'number':
    case 'float':
      if (!!interpolations.length)
        return <NumberInputWithInterpolation value={value} onChange={onChange} interpolations={interpolations} />
      return <NumberInput type="number" step={0.1} value={value} onStopTyping={onChange} {...props} />

    case 'email':
      if (!!interpolations.length)
        return <EmailInputWithInterpolation value={value} onChange={onChange} interpolations={interpolations} />
      return (
        <Input
          iconLeft="BsEnvelope"
          iconSize="1rem"
          type="email"
          value={value}
          onStopTyping={onChange}
          autoComplete="off"
          multiple
          {...props}
        />
      )

    case 'phoneNumber':
      return <Input iconLeft="BsPhone" iconSize="1rem" type="phone" value={value} onStopTyping={onChange} {...props} />

    case 'location':
    case 'address':
      return <AddressInput onChange={onChange} value={value} {...props} />

    case 'place_location':
      return <PlaceSelect onChange={onChange} value={value} type="location" multiple {...props} />

    case 'place_person':
      return <PlaceSelect onChange={onChange} value={value} type="person" multiple {...props} />

    case 'place':
      return <PlaceSelect onChange={onChange} value={value} {...props} />

    case 'asset':
      const hasPlace = place?.id
      const filter = filterByPlace && hasPlace ? { ...props?.filter, placeIds: [place?.id] } : props?.filter
      return <AssetSelect onChange={onChange} value={value} multiple={props.multiple} {...props} filter={filter} />

    case 'user':
      return <UserSelect onChange={onChange} value={value} {...props} />

    case 'tags':
      return <TagsInput value={value} onChange={onChange} />

    case 'pending':
      return <Text>**Campo em desenvolvimento**</Text>

    case 'block':
      return <DynamicBlock value={value} onChange={onChange} {...props} />

    case 'cpf_cnpj':
    case 'text':
      return <InputText value={value} onChange={onChange} masks={masks} interpolations={interpolations} {...props} />

    case 'stock':
      return <StockInput onChange={onChange} value={value} {...props} />

    case 'integration':
      return (
        <DynamicIntegrationInput
          integrationFieldType={integrationFieldType}
          integrationId={integrationId}
          value={value}
          onChange={onChange}
          {...props}
        />
      )

    case 'coordinates':
      return <CoodinatesInput onChange={onChange} value={value} {...props} />

    default:
      return <Textarea value={value} rows={1} onStopTyping={onChange} {...props} />
  }
}

const InputText = ({ value, masks, interpolations, onChange, ...props }) => {
  const SimpleInput = <Input value={value} onStopTyping={onChange} masks={masks} {...props} />

  if (!!interpolations.length)
    return <InputWithInterpolations value={value} onChange={onChange} interpolations={interpolations} />

  if (Array.isArray(masks) && masks.length > 0) return SimpleInput
  if (props.simpleInput && !props.useMarkdown) return SimpleInput

  const hasDefaultValue = props?.defaultValue?.startsWith(':reply')

  return <Textarea value={value} rows={2} onStopTyping={onChange} {...props} disabled={hasDefaultValue} />
}

const DynamicInput = ({
  label,
  forceRequired,
  required,
  options,
  min,
  value,
  valueType,
  onChange,
  allowNull,
  masks,
  interpolations = [],
  displayAsList,
  filterByPlace,
  requiredDoc,
  integrationId,
  integrationFieldType,
  shouldUseImperativeComponent,
  questionId,
  components,
  replies,
  allReplies,
  reply,
  localRef,
  questionReference,
  ...props
}) => {
  const { t } = useTranslation()
  const { place } = useServiceOrder() ?? {}

  const renderInputComponent = () => {
    return renderInput({
      t,
      value,
      valueType,
      options,
      onChange,
      masks,
      allowNull,
      interpolations,
      displayAsList,
      filterByPlace,
      place,
      integrationId,
      integrationFieldType,
      requiredDoc,
      ...props
    })
  }

  return (
    <>
      {shouldUseImperativeComponent ? (
        <ImperativeWrapper
          value={value}
          onChange={onChange}
          questionId={questionId}
          replies={replies}
          reply={reply}
          allReplies={allReplies}
          questionReference={questionReference}
          components={components}
          questionType={valueType}
          allowNull={allowNull}
          ref={el => (components.current[questionId] = el)}
          localRef={localRef}
          {...props}
        >
          {renderInputComponent()}
        </ImperativeWrapper>
      ) : (
        <>{renderInputComponent()}</>
      )}
    </>
  )
}

export default DynamicInput
